import { Module, Mutation, Action } from "vuex-module-decorators";
import PaginationModule from "@/store/base-modules/PaginationModule";
import { RootState } from "@/store";
import { queryRequest } from "@/utils/request";
import { queryDataDictionaryListLang } from "@/graphql/dictionary.gql";
import type { PaginationDataState } from "@/types";
import { WarehouseRecordTypeEnums } from "@/enums/warehouse";
import { httpServer as http } from "@/http/tbrScanAxios";

export interface WarehouseHistorySearchState extends PaginationDataState {
  recordType: string;
  searchCondition: any;
}

const defaultSearchInstance = () => ({
  keyword: "",
  tireCode: ""
});

@Module({ name: "WarehouseHistorySearchModule", namespaced: true })
export default class WarehouseHistorySearchModule
  extends PaginationModule<WarehouseHistorySearchState, RootState>
  implements WarehouseHistorySearchState {
  public listData: any[] = [];
  public recordType: string = "";
  public searchCondition: any = defaultSearchInstance();
  public openFilterFlag: boolean = false;
  public filterData: any = [];

  @Mutation
  setFilterDataMutation(data: any[]) {
    this.filterData = data || [];
  }

  @Mutation
  setRecordTypeMutation(data: string) {
    this.recordType = data;
  }

  @Mutation
  saveListDataMutation(data: any[]) {
    this.listData = data || [];
  }

  @Mutation
  addListDataMutation(data: any[] = []) {
    this.listData.push(...(data || []));
  }

  @Mutation
  setSearchConditionMutation({ key = "", value = "" } = {}) {
    if (!key && !value) {
      this.searchCondition = defaultSearchInstance();
    } else {
      this.searchCondition[key] = value;
    }
  }

  @Mutation
  setOpenFilterFlagMutation(data: boolean) {
    this.openFilterFlag = data ?? false;
  }

  @Action
  async queryListAction({ callback = data => data } = {}) {
    const {
      state: {
        recordType,
        paginationData,
        searchCondition,
        paginationData: { pageNum, pageSize }
      },
      commit
    } = this.context;

    let tempParams;
    switch (recordType) {
      case WarehouseRecordTypeEnums.IN:
        tempParams = { scanType: "IN" };
        break;
      case WarehouseRecordTypeEnums.OUT:
        tempParams = { scanType: "OUT" };
        break;
      case WarehouseRecordTypeEnums.BATCH_OUT:
        tempParams = {
          recordType: WarehouseRecordTypeEnums.OUT
        };
        break;
    }

    const listRes = await http("/k2-scan/app/scan-record/queryPage", {
      ...tempParams,
      attribute: searchCondition.keyword || "",
      tireCode: searchCondition.tireCode || "",
      scanStartTime: searchCondition.startTime || "",
      scanEndTime: searchCondition.endTime || "",
      deductReturnToK1: true,
      pageNum,
      pageSize
    });

    let data = listRes?.data.list || [];
    data = data.map(item => {
      if (!item.detailList || !Array.isArray(item.detailList)) {
        return item;
      }

      const groupedMap = new Map();
      item.detailList.forEach(detail => {
        const key = `${detail.specifications || ""} ${detail.pattern || ""} ${
          detail.level || ""
        }`;

        if (groupedMap.has(key)) {
          groupedMap.get(key).quantity += detail.quantity || 0;
        } else {
          groupedMap.set(key, { ...detail, spe: key });
        }
      });

      return {
        ...item,
        detailList: Array.from(groupedMap.values())
      };
    });

    const totalCount = listRes?.data.total || 0;
    data = await callback(data);
    commit(
      pageNum === 1 ? "saveListDataMutation" : "addListDataMutation",
      data
    );
    commit("savePaginationData", { ...paginationData, totalCount });
  }

  @Action
  async queryFilterDataAction() {
    const data = await queryRequest({
      query: queryDataDictionaryListLang,
      variables: {
        input: {
          ddCode: "BONUS_SOURCE",
          lang: window.i18n.locale
        }
      }
    });
    this.context.commit("setFilterDataMutation", data);
  }
}
